<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useModalStore } from "@comp-lib/stores/modalStore";
import { useUserStore } from "@/stores/userStore";
import StatusBadge from "@comp-lib/components/StatusBadge.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faArrowRightFromBracket, faCheck, faXmark, faShieldKeyhole, faMoneyCheckDollar, faQuestion } from "@fortawesome/pro-regular-svg-icons";
import { storeToRefs } from "pinia";

const modalStore = useModalStore();
const { openModal } = modalStore;

const userStore = useUserStore();
const { userProfile, currentTenant } = storeToRefs(userStore);
const { logUserOut, getPlanName } = userStore;

const planType = computed(() => getPlanName(currentTenant.value?.subscription?.plan_id ?? ""));

const route = useRoute();
const router = useRouter();

const showTenantInfo = computed(() =>
  !route.path.startsWith("/admin") && currentTenant.value !== null
);

const showDropdown = ref(false);
const triggerEl = ref<HTMLElement | null>(null);

const keycloakAccountUrl = computed(() => {
  const dataUserAuth = document.getElementById("app-user-auth")?.dataset;
  if (dataUserAuth?.keycloakUrl && dataUserAuth?.keycloakRealm) {
    return `${dataUserAuth.keycloakUrl}/realms/${dataUserAuth.keycloakRealm}/account`;
  }
  return "";
});

function handleLogout() {
  openModal({
    title: "Log out of your account",
    textContent: "You will be logged out of your account. Are you sure?",
    confirmMethod: () => logUserOut(),
    confirmText: "Yes, log out",
    destructive: true,
  });
}

function checkClickTarget(e: MouseEvent) {
  if (!showDropdown.value) return;
  if (triggerEl.value && !e.composedPath().includes(triggerEl.value)) {
    showDropdown.value = false;
  }
}

watch(
  () => router.currentRoute.value.path,
  () => { showDropdown.value = false }
);

onMounted(() => {
  document.body.addEventListener("click", checkClickTarget);
});
onBeforeUnmount(() => {
  document.body.removeEventListener("click", checkClickTarget);
});
</script>

<template>
  <div v-if="!!userProfile" ref="triggerEl" class="trigger">
    <div :class="['pill', { focus: showDropdown }]" @click="showDropdown = !showDropdown">
      <span class="profile-name">{{ userProfile.given_name }}</span>
      <div class="profile-icon">
        <img src="/src/assets/img/default-profile-avatar.svg" alt="Profile avatar">
      </div>
    </div>

    <div :class="['dropdown', { open: showDropdown }]">
      <div class="profile-fullname">
        <div class="name">{{ userProfile.name }}</div>
        <div class="email">{{ userProfile.email }}</div>
      </div>
      <div v-if="showTenantInfo" class="status-data">
        <p class="item">
          Subscription status:
          <StatusBadge
            v-if="currentTenant?.subscription.is_active"
            color="blue"
            text="Active"
            :statusIcon="faCheck"
          />
          <StatusBadge
            v-else-if="currentTenant?.subscription.is_active === false"
            color="red"
            text="Inactive"
            :statusIcon="faXmark"
          />
          <StatusBadge
            v-else-if="currentTenant?.subscription.is_active === undefined"
            color="gray"
            text="Unknown"
            :statusIcon="faQuestion"
          />
        </p>
        <p class="item">
          Current plan:
          <StatusBadge
            v-if="!planType"
            color="gray"
            text="Unknown"
            :statusIcon="faQuestion"
          />
          <StatusBadge v-if="planType === 'free'" color="teal" text="Free" />
          <StatusBadge v-if="planType === 'qbo'" color="teal" text="QBO" />
          <StatusBadge v-if="planType === 'lite'" color="green" text="Lite" />
          <StatusBadge v-if="planType === 'standard'" color="primary" text="Standard" />
          <RouterLink v-if="planType !== 'standard'" :to="{ name: 'UpdatePlan' }">Upgrade</RouterLink>
        </p>
      </div>
      <div class="actions">
        <RouterLink v-if="showTenantInfo" :to="{ name: 'BillingSettings' }" class="item">
          <span class="icon">
            <FontAwesomeIcon class="icon" :icon="faMoneyCheckDollar" />
          </span>
          Billing
        </RouterLink>
        <a
          :href="keycloakAccountUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="item"
        >
          <span class="icon">
            <FontAwesomeIcon class="icon" :icon="faShieldKeyhole" />
          </span>
          Auth settings
        </a>
        <div class="item logout" @click="handleLogout">
          <span class="icon">
            <FontAwesomeIcon class="icon" :icon="faArrowRightFromBracket" />
          </span>
          Log out
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@mixin open-submenu {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}

.trigger {
  position: relative;
  .pill {
    padding: 5px 5px 5px 20px;
    display: flex;
    gap: 15px;
    align-items: center;
    cursor: pointer;
    border: 1px solid map.get(v.$gray, "300");
    border-radius: map.get(v.$border-radius, "max");
    transition: background-color .2s, box-shadow .2s;
    &:hover, &.focus {
      background-color: map.get(v.$gray, "100");
    }
    &.focus {
      box-shadow: map.get(v.$shadow, "sm");
    }
    .profile-name {
      font-weight: 500;
      color: map.get(v.$gray, "600");
      display: inline-flex;
      justify-content: center;
      align-items: flex-start;
      gap: 5px;
    }
    .profile-icon {
      width: 35px;
      height: 35px;
      border-radius: map.get(v.$border-radius, "max");
      box-shadow: map.get(v.$shadow, "sm");
      @media (max-width: map.get(v.$breakpoints, "md")) {
        width: 30px;
        height: 30px;
      }
    }
  }
  .dropdown {
    visibility: hidden;
    opacity: 0;
    transform: translateY(-10px);
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 10px;
    transition: .3s;
    background-color: white;
    width: 300px;
    border-radius: map.get(v.$border-radius, "sm");
    box-shadow: map.get(v.$shadow, "md");
    &.open {
      @include open-submenu;
    }
    .profile-fullname {
      padding: 15px;
      text-align: center;
      border-bottom: 1px solid map.get(v.$gray, "300");
      .name {
        font-weight: 600;
        font-size: 1.25rem;
        line-height: 1.2;
      }
      .email {
        font-size: .875rem;
        color: map.get(v.$gray, "500");
        line-height: 1.6;
      }
    }
    .status-data {
      padding: 20px 15px;
      border-bottom: 1px solid map.get(v.$gray, "300");
      display: flex;
      flex-direction: column;
      gap: 20px;
      .item {
        display: flex;
        align-items: center;
        gap: 7px;
        a {
          font-size: .75rem;
          font-weight: 400;
        }
      }
    }
    .actions {
      padding: 10px 5px;
      display: flex;
      flex-direction: column;
      .item {
        display: flex;
        align-items: center;
        padding: 8px 10px;
        gap: 10px;
        font-weight: 600;
        color: map.get(v.$gray, "900");
        border-radius: map.get(v.$border-radius, "sm");
        transition: color .2s, background-color .2s;
        &:hover {
          background-color: map.get(v.$primary, "25");
          color: map.get(v.$primary, "600");
          .icon {
            color: map.get(v.$primary, "500");
          }
        }
        .icon {
          color: map.get(v.$gray, "500");
          transition: color .2s;
          width: 20px;
        }
      }
      .logout {
        cursor: pointer;
        color: map.get(v.$error, "700");
        .icon {
          color: map.get(v.$error, "700");
        }
        &:hover {
          background-color: map.get(v.$error, "50");
          color: map.get(v.$error, "700");
          .icon {
            color: map.get(v.$error, "700");
          }
        }
      }
    }
  }
}
</style>
